import React from 'react';
import { Check } from '@phosphor-icons/react';
import onedrawSitePreview from '../../assets/img/pre-contract/onedraw-site-preview.png';
import {
    useOneDrawLayoutImagery,
    useSiteTopViewImagery,
} from '../lib/preContractSiteImagery';

export const ROOF_FACET_COUNT = 4;

/**
 * Crop region for each roof snap — center + size (% of site image).
 * Tuned for demo aerial; for live satellite, center bias is near mid-frame.
 */
export const ROOF_SNAPS: {
    label: string;
    centerX: number;
    centerY: number;
    width: number;
    height: number;
}[] = [
    { label: 'Roof 1', centerX: 50, centerY: 55, width: 28, height: 22 },
    { label: 'Roof 2', centerX: 36, centerY: 50, width: 18, height: 18 },
    { label: 'Roof 3', centerX: 46, centerY: 38, width: 16, height: 14 },
    { label: 'Roof 4', centerX: 58, centerY: 39, width: 16, height: 14 },
];

/**
 * Facet overlays tuned to the oneDRAW 3D site layout snapshot
 * (`onedraw-site-preview.png` / GetDesignImage top-down).
 */
export const ROOF_TOPVIEW_MARKERS: {
    label: string;
    left: number;
    top: number;
    width: number;
    height: number;
    orientation: 'landscape' | 'portrait';
    rotate: number;
    shape: 'rect' | 'gable-n' | 'gable-s' | 'hip';
}[] = [
    {
        label: 'Roof 1',
        left: 36,
        top: 54,
        width: 24,
        height: 16,
        orientation: 'landscape',
        rotate: -4,
        shape: 'rect',
    },
    {
        label: 'Roof 2',
        left: 28,
        top: 52,
        width: 10,
        height: 14,
        orientation: 'portrait',
        rotate: 6,
        shape: 'hip',
    },
    {
        label: 'Roof 3',
        left: 36,
        top: 42,
        width: 12,
        height: 11,
        orientation: 'landscape',
        rotate: -8,
        shape: 'gable-n',
    },
    {
        label: 'Roof 4',
        left: 50,
        top: 41,
        width: 12,
        height: 12,
        orientation: 'portrait',
        rotate: 4,
        shape: 'gable-n',
    },
];

const MAX_SNAP_ZOOM = 200;

function snapStyle(
    index: number,
    siteImageUrl: string,
): React.CSSProperties {
    const snap = ROOF_SNAPS[index];
    const w = snap.width / 100;
    const h = snap.height / 100;
    const zoomW = Math.min(100 / w, MAX_SNAP_ZOOM);
    const zoomH = Math.min(100 / h, MAX_SNAP_ZOOM);

    return {
        backgroundImage: `url(${siteImageUrl})`,
        backgroundSize: `${zoomW.toFixed(2)}% ${zoomH.toFixed(2)}%`,
        backgroundPosition: `${snap.centerX}% ${snap.centerY}%`,
    };
}

function snapMarkerBoxStyle(snap: (typeof ROOF_SNAPS)[number]): React.CSSProperties {
    return {
        left: `${snap.centerX - snap.width / 2}%`,
        top: `${snap.centerY - snap.height / 2}%`,
        width: `${snap.width}%`,
        height: `${snap.height}%`,
    };
}

function layoutMarkerBoxStyle(marker: (typeof ROOF_TOPVIEW_MARKERS)[number]): React.CSSProperties {
    return {
        left: `${marker.left}%`,
        top: `${marker.top}%`,
        width: `${marker.width}%`,
        height: `${marker.height}%`,
        transform: `rotate(${marker.rotate}deg)`,
        transformOrigin: 'center center',
    };
}

type DesignRoofViewportProps = {
    selections: boolean[];
    onToggle: (index: number) => void;
    /** Install address used to load live satellite imagery for card snaps. */
    siteAddress?: string;
    /** Opportunity id — used to prefer oneDRAW design layout imagery for snaps. */
    salesForceId?: string;
    /** Live ortho snapshot from the embedded 3D viewer (overrides other imagery when set). */
    snapshotUrl?: string | null;
    /**
     * - `cards` (default): roof snap cards (+ optional site backdrop when not compact)
     * - `aerial`: full-bleed oneDRAW 3D site layout with clickable roof markers
     */
    mode?: 'cards' | 'aerial';
    /** Cards mode only: 2×2 roof snaps without site backdrop / markers. */
    compact?: boolean;
};

export const DesignRoofViewport: React.FC<DesignRoofViewportProps> = ({
    selections,
    onToggle,
    siteAddress = '',
    salesForceId = '',
    snapshotUrl = null,
    mode = 'cards',
    compact = false,
}) => {
    const useLayoutForCards = mode === 'cards' && Boolean(salesForceId.trim()) && !snapshotUrl;
    const satelliteImagery = useSiteTopViewImagery(
        mode === 'cards' && !useLayoutForCards && !snapshotUrl ? siteAddress : '',
    );
    const layoutImagery = useOneDrawLayoutImagery(
        mode === 'aerial' || useLayoutForCards ? salesForceId : '',
    );

    if (mode === 'aerial') {
        const layoutUrl = snapshotUrl || layoutImagery.url || onedrawSitePreview;
        const panelStyle = {
            '--pci-site-preview': `url(${layoutUrl})`,
        } as React.CSSProperties;

        return (
            <div className="pci-roof-panel pci-roof-panel--aerial" style={panelStyle}>
                <div className="pci-roof-panel__aerial-photo" aria-hidden />
                {layoutImagery.loading && !snapshotUrl ? (
                    <p className="pci-roof-panel__aerial-status">Loading oneDRAW layout…</p>
                ) : null}
                <div className="pci-roof-panel__markers pci-roof-panel__markers--interactive">
                    {ROOF_TOPVIEW_MARKERS.map((marker, index) => {
                        const selected = selections[index];
                        return (
                            <button
                                key={`marker-${marker.label}`}
                                type="button"
                                className={`pci-roof-panel__marker-box pci-roof-panel__marker-box--layout${
                                    selected ? ' pci-roof-panel__marker-box--on' : ''
                                }`}
                                style={layoutMarkerBoxStyle(marker)}
                                onClick={() => onToggle(index)}
                                aria-label={marker.label}
                                aria-pressed={selected}
                            >
                                <span
                                    className="pci-roof-panel__marker-num"
                                    style={{ transform: `rotate(${-marker.rotate}deg)` }}
                                >
                                    {index + 1}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    const siteImageUrl =
        snapshotUrl ||
        (useLayoutForCards ? layoutImagery.url : satelliteImagery.url) ||
        onedrawSitePreview;
    const panelStyle = {
        '--pci-site-preview': `url(${siteImageUrl})`,
    } as React.CSSProperties;

    return (
        <div
            className={`pci-roof-panel${compact ? ' pci-roof-panel--compact' : ''}`}
            style={panelStyle}
        >
            {!compact ? (
                <>
                    <div className="pci-roof-panel__backdrop" aria-hidden />
                    <div className="pci-roof-panel__markers" aria-hidden>
                        {ROOF_SNAPS.map((snap, index) => (
                            <span
                                key={`marker-${snap.label}`}
                                className={`pci-roof-panel__marker-box${
                                    selections[index] ? ' pci-roof-panel__marker-box--on' : ''
                                }`}
                                style={snapMarkerBoxStyle(snap)}
                            >
                                <span className="pci-roof-panel__marker-num">{index + 1}</span>
                            </span>
                        ))}
                    </div>
                    <div className="pci-roof-panel__scrim" aria-hidden />
                </>
            ) : null}

            <div className="pci-roof-panel__grid">
                {ROOF_SNAPS.map((snap, index) => {
                    const selected = selections[index];
                    return (
                        <button
                            key={snap.label}
                            type="button"
                            className={`pci-roof-card${selected ? ' pci-roof-card--on' : ''}`}
                            onClick={() => onToggle(index)}
                            aria-label={snap.label}
                            aria-pressed={selected}
                        >
                            <div className="pci-roof-card__snap" style={snapStyle(index, siteImageUrl)}>
                                <span className="pci-roof-card__snap-num">{index + 1}</span>
                            </div>
                            <div className="pci-roof-card__meta">
                                <span className="pci-roof-card__num">{index + 1}</span>
                                <span className="pci-roof-card__label">{snap.label}</span>
                                {selected ? (
                                    <Check className="pci-roof-card__check" size={14} weight="bold" />
                                ) : null}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default DesignRoofViewport;
